import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { crypto as cryptoShared } from '@photo-vault/shared';
import type { dto } from '@photo-vault/shared';
import { fromBase64Url } from '@salvobee/crypto-vault';
import { AntiAutofillHoneypot, Button, Card, Icon, Input, Tag } from '../../ui/index.js';
import { useAuth } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { ApiError } from '../../lib/api.js';
import { stashRecoveryCode } from './recoveryStorage.js';

type Mode = 'unknown' | 'existing' | 'new';
type Phase = 'bootstrap' | 'awaiting-input' | 'finalising' | 'done' | 'error';

interface BootstrapState {
  mode: Mode;
  jwt: string;
  email: string;
  bootstrap?: dto.LoginBootstrapResponse;
}

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { auth, metadata } = useInfra();

  const [phase, setPhase] = useState<Phase>('bootstrap');
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<BootstrapState | null>(null);

  const [passphrase, setPassphrase] = useState('');
  const [passphraseConfirm, setPassphraseConfirm] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const oidc = await auth.completeFederatedLogin();
        try {
          const bootstrap = await metadata.loginBootstrap(oidc.idToken);
          if (cancelled) return;
          setState({ mode: 'existing', jwt: oidc.idToken, email: oidc.email, bootstrap });
        } catch (err) {
          if (err instanceof ApiError && err.status === 404 && err.code === 'USER_NOT_FOUND') {
            if (cancelled) return;
            setState({ mode: 'new', jwt: oidc.idToken, email: oidc.email });
          } else {
            throw err;
          }
        }
        if (cancelled) return;
        setPhase('awaiting-input');
      } catch (err) {
        if (cancelled) return;
        setPhase('error');
        setError(err instanceof Error ? err.message : 'Errore durante il login federato.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleExistingUnlock(event: React.FormEvent) {
    event.preventDefault();
    if (!state || state.mode !== 'existing' || !state.bootstrap) return;
    setError(null);
    setPhase('finalising');
    try {
      const salt = fromBase64Url(state.bootstrap.saltB64);
      const { masterKey, verifierB64 } = await cryptoShared.deriveMaster(passphrase, salt);
      const verify = await metadata.verifyPassphrase(
        { verifierB64 } satisfies dto.VerifyPassphraseRequest,
        state.jwt,
      );
      if (!verify.ok) throw new Error('Passphrase non corretta.');
      const rsaPriv = await cryptoShared.unwrapPrivateKey(state.bootstrap.wrappedPriv, masterKey);
      setSession({
        userId: state.bootstrap.userId,
        email: state.bootstrap.email,
        jwt: state.jwt,
        masterKey,
        rsaPriv,
        rsaPubB64: state.bootstrap.rsaPubB64,
        saltB64: state.bootstrap.saltB64,
        provider: 'federated',
      });
      setPhase('done');
      navigate('/', { replace: true });
    } catch (err) {
      setPhase('awaiting-input');
      setError(err instanceof Error ? err.message : 'Sblocco fallito.');
    }
  }

  async function handleNewSetup(event: React.FormEvent) {
    event.preventDefault();
    if (!state || state.mode !== 'new') return;
    setError(null);
    if (passphrase !== passphraseConfirm) {
      setError('Le due passphrase non coincidono.');
      return;
    }
    if (passphrase.length < 12) {
      setError('La passphrase deve essere di almeno 12 caratteri.');
      return;
    }
    setPhase('finalising');
    try {
      const setup = await cryptoShared.generateIdentity(passphrase);
      const body: dto.RegisterRequest = setup.identity;
      const registered = await metadata.registerIdentity(body, state.jwt);
      setSession({
        userId: registered.userId,
        email: registered.email,
        jwt: state.jwt,
        masterKey: setup.masterKey,
        rsaPriv: setup.rsaKeys.privateKey,
        rsaPubB64: setup.identity.rsaPubB64,
        saltB64: setup.identity.saltB64,
        provider: 'federated',
      });
      stashRecoveryCode(setup.recoveryCode, 'signup');
      setPhase('done');
      navigate('/recovery-code', { replace: true });
    } catch (err) {
      setPhase('awaiting-input');
      setError(err instanceof Error ? err.message : 'Registrazione fallita.');
    }
  }

  if (phase === 'bootstrap') {
    return (
      <div className="mx-auto flex max-w-md flex-col gap-6 py-6">
        <Tag withDot>federated sign-in</Tag>
        <Card>
          <p className="text-on-surface-variant">Completo il login con il provider…</p>
        </Card>
      </div>
    );
  }

  if (phase === 'error' || !state) {
    return (
      <div className="mx-auto flex max-w-md flex-col gap-6 py-6">
        <Tag withDot>federated sign-in</Tag>
        <Card className="flex flex-col gap-4">
          <p className="text-error">{error ?? 'Errore sconosciuto.'}</p>
          <Button onClick={() => navigate('/login')}>Torna al login</Button>
        </Card>
      </div>
    );
  }

  const busy = phase === 'finalising';

  return (
    <div className="mx-auto flex max-w-md flex-col gap-6 py-6">
      <div className="flex flex-col gap-3">
        <Tag withDot>federated sign-in · {state.email}</Tag>
        <h2 className="font-display text-3xl font-semibold leading-tight md:text-4xl">
          {state.mode === 'existing' ? (
            <>
              Sblocca il
              <br />
              <span className="text-cyan drop-shadow-[0_0_8px_rgba(100,255,218,0.4)]">
                tuo vault
              </span>
            </>
          ) : (
            <>
              Crea la tua
              <br />
              <span className="text-cyan drop-shadow-[0_0_8px_rgba(100,255,218,0.4)]">
                passphrase E2EE
              </span>
            </>
          )}
        </h2>
        {state.mode === 'new' ? (
          <p className="max-w-md text-on-surface-variant">
            L'identità federata ha autenticato la sessione, ma la passphrase E2EE non lascia mai il
            dispositivo. Generiamo qui chiavi e codice di recupero.
          </p>
        ) : null}
      </div>

      <Card className="flex flex-col gap-6">
        <form
          className="flex flex-col gap-3"
          onSubmit={state.mode === 'existing' ? handleExistingUnlock : handleNewSetup}
          autoComplete="off"
        >
          <AntiAutofillHoneypot />
          <Input
            type="password"
            label="Passphrase E2EE"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            leftIcon={<Icon name="lock" className="text-base" />}
            autoComplete={state.mode === 'existing' ? 'current-password' : 'new-password'}
            required
          />
          {state.mode === 'new' ? (
            <Input
              type="password"
              label="Conferma passphrase"
              value={passphraseConfirm}
              onChange={(e) => setPassphraseConfirm(e.target.value)}
              leftIcon={<Icon name="check" className="text-base" />}
              autoComplete="new-password"
              required
            />
          ) : null}

          {error ? (
            <div className="rounded-lg border border-error/60 bg-error-container/30 px-4 py-3 text-sm text-error">
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            size="lg"
            loading={busy}
            rightIcon={!busy ? <Icon name="arrow_forward" className="text-base" /> : null}
          >
            {state.mode === 'existing' ? 'Sblocca il vault' : 'Crea il vault'}
          </Button>

          {state.mode === 'existing' ? (
            <p className="pt-1 text-center text-sm text-on-surface-variant">
              <button
                type="button"
                className="font-semibold text-cyan transition-colors hover:underline"
                onClick={() => navigate('/recovery')}
                disabled={busy}
              >
                Passphrase E2EE dimenticata?
              </button>
            </p>
          ) : null}
        </form>
      </Card>
    </div>
  );
}
